"use client";

import { useState } from "react";
import {
  Checkbox,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  CalculatePanel,
  CountPanel,
  RoundPanel,
} from "@/components/tools/math/sig-fig-panels";
import { useToolUi } from "@/components/tools/tool-ui";
import type { RoundingMode, SigOperation } from "@/lib/tool-kit/math/sig-figs";

/** English strings. A translated content file overrides these through `ui`. */
export const SIGNIFICANT_FIGURES_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to do?",
  modeCount: "Count significant figures",
  modeRound: "Round to significant figures",
  modeCalculate: "Calculate with significant figures",
  number: "Number",
  numberPlaceholder: "0.004050 or 6.02 × 10^23",
  numberHint:
    "Scientific notation works too: 6.02e23, 6.02 × 10^23 or 6.02×10²³.",
  firstNumber: "First number",
  secondNumber: "Second number",
  exact: "Exact number (counted or defined)",
  operation: "Operation",
  operations: {
    add: "+ Add",
    subtract: "− Subtract",
    multiply: "× Multiply",
    divide: "÷ Divide",
  },
  target: "Round to",
  roundingRule: "When the dropped part is exactly one half",
  ruleHalfUp: "Round up (most textbooks)",
  ruleHalfEven: "Round to the even digit (banker's rounding)",
  results: "Results",
  sigFigs: "Significant figures",
  decimalPlaces: "Decimal places",
  scientific: "Scientific notation",
  eNotation: "E-notation",
  digitByDigit: "Digit by digit",
  legendSignificant: "Significant",
  legendNot: "Not significant",
  legendAmbiguous: "Ambiguous",
  digitColumn: "Digit",
  placeColumn: "Place",
  significantColumn: "Significant?",
  whyColumn: "Why",
  yes: "Yes",
  no: "No",
  maybe: "Ambiguous",
  reasons: {
    nonzero: "Nonzero digits are always significant.",
    leading: "A leading zero only locates the decimal point.",
    captive: "A zero between nonzero digits is significant.",
    "trailing-point":
      "A trailing zero in a number with a decimal point is significant.",
    "trailing-ambiguous":
      "A trailing zero with no decimal point is ambiguous, so it is not counted.",
  },
  sigFigsCount:
    "one: {count} significant figure|other: {count} significant figures",
  placesCount: "one: {count} decimal place|other: {count} decimal places",
  trailingZeros: "one: trailing zero|other: {count} trailing zeros",
  ambiguousNote:
    "{number} has {count} for certain. Its {zeros} could be measured digits or placeholders; if they were measured, write {forms}.",
  orJoin: "{first} or {second}",
  zeroNote:
    "Zero has no nonzero digit, so the counting rules give it no significant figures. What matters when you add or subtract it is its precision: {places}.",
  commaNote:
    "“{typed}” was read as {read}: a single comma is taken as a decimal comma. If you meant a whole number, type it without the comma.",
  invalid:
    "That is not a number this tool can read. Try 0.0450, 1200, 6.02e23 or 6.02 × 10^23.",
  tooLong: "That number is too long: up to 60 digits and exponents up to ±400.",
  enterCount: "Type a number to count its significant figures.",
  enterRound: "Type a number to round it.",
  enterTwo: "Enter two numbers to calculate with significant figures.",
  rounded: "Rounded",
  howRounded: "How it was rounded",
  stepCount:
    "Count from the first nonzero digit: the first {n} significant digits are {kept}.",
  stepDecider: "The next digit is {digit}, so round {direction}.",
  stepHalfEven:
    "The dropped part is exactly one half (a 5 with only zeros after it), so round to the even digit: {direction}.",
  up: "up",
  down: "down",
  stepCarry:
    "Rounding up carried into the next place, so the kept digits move up a place: {result}",
  stepPad:
    "{number} has only {have}, so {padding} added to reach {n}. Add zeros only if they are real, measured digits.",
  paddingZeros: "one: one zero was|other: {count} zeros were",
  stepResult: "Result: {result}",
  trailingPointNote:
    "The decimal point at the end of {text} marks its final zero as significant.",
  ambiguousResultNote:
    "Written out, {text} looks as if it has fewer significant figures. Write {scientific} to show all {n}.",
  plainTooLong: "Too long to write out in full; use the scientific form.",
  copyResult: "Copy result",
  copied: "Copied!",
  answer: "Answer",
  unrounded: "Unrounded (calculator display)",
  sigFigsInAnswer: "Significant figures in the answer",
  working: "The working",
  operandMeasured: "{number}: {count}, last significant digit in the {place}.",
  operandExact: "{number}: exact, so it does not limit the answer.",
  operandZero:
    "{number}: zero, with no significant figures but precise to the {place}.",
  ruleMultiply:
    "Multiplication and division: keep as many significant figures as the measurement with the fewest: {count}.",
  ruleAdd:
    "Addition and subtraction: keep the last decimal place of the least precise measurement: the {place}.",
  ruleExact:
    "Both numbers are exact, so the answer is exact too, with no rounding.",
  ruleZero: "The answer is zero, which has no significant figures to round.",
  unroundedLine: "Unrounded: {expression} = {value}",
  roundedLine: "Rounded: {value}",
  repeatingNote:
    "This quotient does not terminate; the calculator display shows its first 15 digits.",
  ambiguousOperand:
    "{number} was read with {count} because its trailing zeros are ambiguous. If they were measured, write {forms}.",
  roundOnceTip:
    "In a multi-step calculation, carry every digit through the intermediate steps and round only the final answer.",
  divideByZero: "Division by zero is undefined.",
  places: {
    "-6": "millionths place",
    "-5": "hundred-thousandths place",
    "-4": "ten-thousandths place",
    "-3": "thousandths place",
    "-2": "hundredths place",
    "-1": "tenths place",
    "0": "ones place",
    "1": "tens place",
    "2": "hundreds place",
    "3": "thousands place",
    "4": "ten-thousands place",
    "5": "hundred-thousands place",
    "6": "millions place",
  },
  placeOther: "10{power} place",
} as const;

type Mode = "count" | "round" | "calculate";

const TARGETS = Array.from({ length: 15 }, (_, i) => String(i + 1));
const OPERATIONS: readonly SigOperation[] = [
  "add",
  "subtract",
  "multiply",
  "divide",
];
export default function SignificantFiguresCalculatorWidget() {
  const strings = useToolUi(SIGNIFICANT_FIGURES_CALCULATOR_STRINGS);
  const [mode, setMode] = useState<Mode>("count");
  const [number, setNumber] = useState("0.004050");
  const [target, setTarget] = useState("3");
  const [rule, setRule] = useState<RoundingMode>("half-up");
  const [a, setA] = useState("2.5");
  const [b, setB] = useState("3.42");
  const [exactA, setExactA] = useState(false);
  const [exactB, setExactB] = useState(false);
  const [operation, setOperation] = useState<SigOperation>("multiply");

  const ruleSelect = (
    <SelectField
      id="sf-rule"
      label={strings.roundingRule}
      onChange={setRule}
      options={[
        { value: "half-up", label: strings.ruleHalfUp },
        { value: "half-even", label: strings.ruleHalfEven },
      ]}
      value={rule}
    />
  );

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="sf-mode"
        onChange={setMode}
        options={[
          { value: "count", label: strings.modeCount },
          { value: "round", label: strings.modeRound },
          { value: "calculate", label: strings.modeCalculate },
        ]}
        value={mode}
      />
      {mode === "calculate" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <TextField
              id="sf-a"
              label={strings.firstNumber}
              mono
              onChange={setA}
              value={a}
            />
            <Checkbox
              checked={exactA}
              label={strings.exact}
              onChange={setExactA}
            />
          </div>
          <div className="space-y-2">
            <TextField
              id="sf-b"
              label={strings.secondNumber}
              mono
              onChange={setB}
              value={b}
            />
            <Checkbox
              checked={exactB}
              label={strings.exact}
              onChange={setExactB}
            />
          </div>
          <SelectField
            id="sf-operation"
            label={strings.operation}
            onChange={setOperation}
            options={OPERATIONS.map((op) => ({
              value: op,
              label: strings.operations[op],
            }))}
            value={operation}
          />
          {ruleSelect}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            hint={strings.numberHint}
            id="sf-number"
            label={strings.number}
            mono
            onChange={setNumber}
            placeholder={strings.numberPlaceholder}
            value={number}
          />
          {mode === "round" ? (
            <div className="grid grid-cols-2 gap-4">
              <SelectField
                id="sf-target"
                label={strings.target}
                onChange={setTarget}
                options={TARGETS.map((value) => ({ value, label: value }))}
                value={target}
              />
              {ruleSelect}
            </div>
          ) : null}
        </div>
      )}
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {mode === "count" ? (
          <CountPanel strings={strings} text={number} />
        ) : null}
        {mode === "round" ? (
          <RoundPanel
            rule={rule}
            strings={strings}
            target={Number(target)}
            text={number}
          />
        ) : null}
        {mode === "calculate" ? (
          <CalculatePanel
            a={a}
            b={b}
            exactA={exactA}
            exactB={exactB}
            operation={operation}
            rule={rule}
            strings={strings}
          />
        ) : null}
      </section>
    </>
  );
}
